import { appRouteFamilyPatterns } from "@/utils/app-route-family-patterns";

type RoutePatternMatch = {
  dynamicSegments: number;
  pattern: string;
  staticSegments: number;
};

function getPathname(value: string): string {
  try {
    return new URL(value, "https://atlas.invalid").pathname;
  } catch {
    return value.split("?")[0]?.split("#")[0] ?? "/";
  }
}

function getSegments(pathname: string): string[] {
  return pathname
    .split("/")
    .map(segment => segment.trim())
    .filter(Boolean)
    .slice(0, 12);
}

function isDynamicPatternSegment(segment: string): boolean {
  return /^\[[\w.-]+\]$/.test(segment);
}

function isCatchAllPatternSegment(segment: string): boolean {
  return /^\[\.\.\.[\w.-]+\]$/.test(segment);
}

function matchRoutePattern(pattern: string, pathnameSegments: readonly string[]): RoutePatternMatch | null {
  if (pattern === "/") {
    return pathnameSegments.length === 0
      ? { dynamicSegments: 0, pattern, staticSegments: 0 }
      : null;
  }

  const patternSegments = getSegments(pattern);
  let staticSegments = 0;
  let dynamicSegments = 0;

  for (let index = 0; index < patternSegments.length; index += 1) {
    const patternSegment = patternSegments[index];
    const pathnameSegment = pathnameSegments[index];

    if (!patternSegment) {
      return null;
    }

    if (isCatchAllPatternSegment(patternSegment)) {
      return {
        dynamicSegments: dynamicSegments + Math.max(1, pathnameSegments.length - index),
        pattern,
        staticSegments,
      };
    }

    if (!pathnameSegment) {
      return null;
    }

    if (isDynamicPatternSegment(patternSegment)) {
      dynamicSegments += 1;
      continue;
    }

    if (patternSegment !== pathnameSegment) {
      return null;
    }

    staticSegments += 1;
  }

  if (patternSegments.length !== pathnameSegments.length) {
    return null;
  }

  return {
    dynamicSegments,
    pattern,
    staticSegments,
  };
}

function compareRoutePatternMatch(left: RoutePatternMatch, right: RoutePatternMatch): number {
  if (left.staticSegments !== right.staticSegments) {
    return right.staticSegments - left.staticSegments;
  }

  if (left.dynamicSegments !== right.dynamicSegments) {
    return left.dynamicSegments - right.dynamicSegments;
  }

  return left.pattern.length - right.pattern.length;
}

function findBestRouteFamily(pathnameSegments: readonly string[]): string | null {
  const matches = appRouteFamilyPatterns
    .map(pattern => matchRoutePattern(pattern, pathnameSegments))
    .filter((match): match is RoutePatternMatch => match !== null)
    .sort(compareRoutePatternMatch);

  return matches[0]?.pattern ?? null;
}

export function normalizeClientRouteFamily(value: string | null | undefined): string {
  if (!value) {
    return "/";
  }

  const pathnameSegments = getSegments(getPathname(value));
  return findBestRouteFamily(pathnameSegments) ?? "/unmatched";
}
